import React from 'react';
import { UserCircle } from '@phosphor-icons/react';

import { type ConversationSummary } from './types';

import { ChatLogo } from '@/components/logo';
import { cn } from '@/lib/utils';

type SelectConversationFn = (params: { conversationId: string; isHandoff: boolean; isCompleted: boolean }) => void;

type HistoryPageProps = {
  conversations: ConversationSummary[];
  isLoading: boolean;
  onSelectConversation?: SelectConversationFn;
};

function ConversationAvatar({ isHandoff, agentAvatarUrl }: { isHandoff?: boolean; agentAvatarUrl?: string | null }) {
  if (isHandoff) {
    return agentAvatarUrl ? (
      <img src={agentAvatarUrl} className="h-8 w-8 shrink-0 rounded-full object-cover" alt="Agent avatar" />
    ) : (
      <UserCircle size={32} className="text-primary" />
    );
  }

  return <ChatLogo className="border border-neutral-200 rounded-full" />;
}

function ConversationRow({ item, onSelect }: { item: ConversationSummary; onSelect: SelectConversationFn }) {
  return (
    <button
      onClick={() =>
        onSelect({
          conversationId: item.conversationId,
          isHandoff: item.isHandoff ?? false,
          isCompleted: item.status === 'completed',
        })
      }
      className={cn(
        'w-full flex items-center gap-3 p-3 rounded-lg bg-card text-left',
        'hover:bg-accent transition-colors',
        'border-l-4 border-transparent hover:border-primary/40',
      )}
    >
      <div className="relative shrink-0">
        <ConversationAvatar isHandoff={item.isHandoff} agentAvatarUrl={item.agentAvatarUrl} />
        {item.hasUnread && <span className="absolute top-0 right-0 size-2 rounded-full bg-red-500" />}
      </div>
      <div className="flex flex-col min-w-0">
        <span className="text-sm font-medium truncate">{item.title}</span>
        <span className="text-xs text-muted-foreground truncate">
          {item.subtitle ? `${item.subtitle} replied • ${item.timestamp}` : item.timestamp}
        </span>
      </div>
    </button>
  );
}

function ConversationSection({
  title,
  items,
  onSelect,
  isReadOnly = false,
}: {
  title: string;
  items: ConversationSummary[];
  onSelect: SelectConversationFn;
  isReadOnly?: boolean;
}) {
  if (items.length === 0) return null;

  return (
    <section>
      <div className="flex items-center gap-2 mb-2">
        <p className="text-sm text-muted-foreground">{title}</p>
        {isReadOnly && (
          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
            View only
          </span>
        )}
      </div>
      <div className="flex flex-col gap-2">
        {items.map((item) => (
          <ConversationRow key={item.conversationId} item={item} onSelect={onSelect} />
        ))}
      </div>
    </section>
  );
}

function ConversationRowSkeleton() {
  return (
    <div className="w-full flex items-center gap-3 p-3 rounded-lg bg-card animate-pulse">
      <div className="size-8 rounded-full bg-muted shrink-0" />
      <div className="flex flex-col gap-2 flex-1 min-w-0">
        <div className="h-3.5 bg-muted rounded w-3/4" />
        <div className="h-3 bg-muted rounded w-1/2" />
      </div>
    </div>
  );
}

const noop: SelectConversationFn = () => {};

export function HistoryPage({ conversations, isLoading, onSelectConversation }: HistoryPageProps) {
  const handleSelect = onSelectConversation ?? noop;
  const active = conversations.filter((c) => c.status === 'active');
  const previous = conversations.filter((c) => c.status === 'completed');

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 px-3 py-4 overflow-hidden">
        <div className="h-4 w-16 bg-muted rounded animate-pulse mb-2" />
        {[...Array(3)].map((_, i) => (
          <ConversationRowSkeleton key={i} />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 px-3 py-4 min-h-full">
      <ConversationSection title="Active" items={active} onSelect={handleSelect} />
      <ConversationSection title="Previous" items={previous} onSelect={handleSelect} isReadOnly />

      {conversations.length === 0 && (
        <div className="flex flex-col items-center justify-center gap-3 mt-16 text-center px-6">
          <ChatLogo size={40} className="text-muted-foreground opacity-40" />
          <p className="text-sm font-medium text-foreground">No Threads yet</p>
          <p className="text-xs text-muted-foreground">Start a conversation and it will appear here.</p>
        </div>
      )}
    </div>
  );
}
